import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "size", "disableAutoPositioning"];
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { useCrossAppID, useCrossAppSearch } from '../crossAppID';
import Brand from '../Brand';
import Boundary from '../Boundary';
import Dropdown from '../Dropdown';
import { StickyBarGroup } from '../StickyBar';
import { sizes } from './constants';
import NavbarSection from './NavbarSection';
import NavbarItem from './NavbarItem';
import NavbarAccountProvider from './NavbarAccountProvider';
import NavbarAccountItem from './NavbarAccountItem';
import NavbarAccountInfo from './NavbarAccountInfo';
const DISPLAY_NAME = 'Navbar';
const DEFAULT_HEIGHT = '64px';
const SMALL_HEIGHT = '44px';
const DROPDOWN_MAX_HEIGHT = 430;
const Root = styled.header.withConfig({
  displayName: "Navbar__Root",
  componentId: "sc-gmzmi2-0"
})(["display:flex;align-items:stretch;width:100%;height:", ";background-color:var(--neutral-surface);box-shadow:var(--shadow-sm);will-change:top;transition-property:top;transition-duration:var(--motion-duration-normal);transition-timing-function:var(--motion-timing-function-ease-in-out);", " ", " ", " &,> *{display:flex;flex-wrap:nowrap;}", "{order:0;padding-right:var(--size-spacing-04);padding-left:var(--size-spacing-04);height:auto;> *{height:32px;}}"], DEFAULT_HEIGHT, _ref => {
  let {
    $size
  } = _ref;
  return $size === sizes.sm && css(["height:", ";"], SMALL_HEIGHT);
}, _ref2 => {
  let {
    $autoPositioning
  } = _ref2;
  return $autoPositioning && css(["position:fixed;width:100vw;top:0;left:0;z-index:var(--zindex-2);"]);
}, _ref3 => {
  let {
    $autoPositioning,
    $stickyBarHeight
  } = _ref3;
  return $autoPositioning && $stickyBarHeight && css(["top:", "px;"], $stickyBarHeight);
}, Brand.Style);
const Navbar = /*#__PURE__*/forwardRef((_ref4, ref) => {
  let {
      children,
      size,
      disableAutoPositioning
    } = _ref4,
    rest = _objectWithoutPropertiesLoose(_ref4, _excluded);
  const [crossAppProps] = useCrossAppID(DISPLAY_NAME, {
    disabled: disableAutoPositioning
  });
  const [stickyBarGroupData] = useCrossAppSearch(StickyBarGroup, disableAutoPositioning ? [] : ['height']);
  return /*#__PURE__*/React.createElement(Boundary, {
    name: DISPLAY_NAME
  }, /*#__PURE__*/React.createElement(Dropdown.Provider, {
    maxHeight: DROPDOWN_MAX_HEIGHT
  }, /*#__PURE__*/React.createElement(Root, _extends({}, rest, crossAppProps, {
    ref: ref,
    $size: size,
    $stickyBarHeight: stickyBarGroupData === null || stickyBarGroupData === void 0 ? void 0 : stickyBarGroupData.height,
    $autoPositioning: !disableAutoPositioning
  }), children)));
});
Navbar.displayName = DISPLAY_NAME;
Navbar.Style = Root;
Navbar.Section = NavbarSection;
Navbar.Item = NavbarItem;
Navbar.AccountProvider = NavbarAccountProvider;
Navbar.AccountItem = NavbarAccountItem;
Navbar.AccountInfo = NavbarAccountInfo;
Navbar.sizes = sizes;
Navbar.defaultProps = {
  size: Navbar.sizes.md,
  disableAutoPositioning: false
};
process.env.NODE_ENV !== "production" ? Navbar.propTypes = {
  /**
   * Sets the children.
   */
  children: PropTypes.node,
  /**
   * Sets the Navbar size.
   */
  size: PropTypes.oneOf([Navbar.sizes.md, Navbar.sizes.sm]),
  /**
   * Disables automatic positioning of the component.
   * If this property is `true`, the component positioning must be provided by some external means.
   */
  disableAutoPositioning: PropTypes.bool
} : void 0;
export default Navbar;